import type { ConnectorStatus } from './connectors';
import { CHANNEL_SLUGS, connectorsPath } from './connectors-route';

/**
 * Where a `requiresConnect` suggested-task chip's tap should LAND — the Connectors page, scrolled to
 * the specific connector the task would use, on whichever tab actually holds it (a channel like
 * WhatsApp lives under `channels`, everything else under `tools`).
 *
 * Before this, a `requiresConnect` chip did exactly what a ready chip does: prefill the composer and
 * wait for Send. Sending a prompt for an app that isn't connected yet only pushed the "please connect
 * X" ask one turn later, into the conversation — the chip's own "Connect" marker already told the
 * reader this wasn't ready to run, so the tap should take them to the place that fixes that, not to a
 * text box that's going to bounce. Absent `connector` has nowhere to land, so this is `null` and the
 * caller falls back to the ordinary prefill (which is also the null case's only sane behaviour).
 */
export function connectHrefFor(connector: string): string | null {
  if (!connector) return null;
  const tab = (CHANNEL_SLUGS as readonly string[]).includes(connector) ? 'channels' : 'tools';
  return connectorsPath({ tab, target: connector });
}

/**
 * What a `requiresConnect` chip's tap should DO, decided purely (so it is testable without the DOM or
 * the connectors data layer). Three outcomes, in order of preference:
 *
 * - **`connect`** — the task's connector resolves to a real `ConnectorStatus` in the (prefetched) list,
 *   so the chip runs the actual connect flow in place, the same rail the Connectors page dispatches.
 * - **`navigate`** — the slug names a connector we cannot resolve here yet (the list hasn't loaded, or an
 *   unknown slug), so we fall back to today's behavior: send the reader to that connector's card on the
 *   Connectors page (`connectHrefFor`). Never a dead tap.
 * - **`prefill`** — the task names no connector at all (nowhere to land), so the chip degrades to the
 *   ordinary prefill, exactly as a chip that was never marked `requiresConnect`.
 */
export type SuggestedConnectAction =
  | { kind: 'connect'; connector: ConnectorStatus }
  | { kind: 'navigate'; href: string }
  | { kind: 'prefill' };

export function resolveSuggestedConnect(connector: string, connectors: ConnectorStatus[] | undefined): SuggestedConnectAction {
  const match = connector ? connectors?.find((c) => c.toolkit === connector) : undefined;
  if (match) return { kind: 'connect', connector: match };
  const href = connectHrefFor(connector);
  return href ? { kind: 'navigate', href } : { kind: 'prefill' };
}
